import { instantFromIso } from "@nijiurachan/js/util/time";
import type { Catalog, ThreadSummary } from "@/entities/thread";
import type { SortType } from "@/features/catalog/stores/catalogStore";
import type { MixSelection } from "@/features/settings/stores/mixStore";

/** 混合板1枚あたりの取得計画。limitは取り分(%)から導いた枠数。 */
export type MixExtraFetchPlan = Readonly<{
  slug: string;
  limit: number;
}>;

/**
 * 取り分(%)をページ枠数へ換算する。有効にした板が小さいページ設定で
 * 0枚に丸まって見えなくなるのを避けるため最低1枠を保証する。
 */
export function planMixExtraLimit(pageLimit: number, pct: number): number {
  return Math.max(1, Math.min(pageLimit, Math.round((pageLimit * pct) / 100)));
}

export type MixFetchPlan = Readonly<{
  /**
   * catalog-mixへ要求する枠数。ページ枠から取り分の合計を引いた残り。
   * 制御なしの板が1つも無い構成ではnullになり、catalog-mixを呼ばない。
   */
  uncappedLimit: number | null;
  extras: readonly MixExtraFetchPlan[];
}>;

/**
 * 総合カタログ1ページの取得計画。各板の枠数を**取得前に**確定させる。
 *
 * 制御なし群をページ全枠で取得してから削る方式にはしない。削った分は
 * 次ページのoffsetに含まれないため、page1で捨てた制御なし群のスレが
 * page2以降で二度と現れない(ページが連続しない)ため。
 * 制御なしの板があるときは、取り分の合計がページ枠を食い尽くさないよう
 * 最低1枠を残す。制御なしの板が無いときは%が純粋な上限になり、
 * ページは取り分の合計までしか埋まらない。全板に1枠ずつ割り当てられない
 * 構成では各板を固定1枠で取得し、呼び出し側で複数の表示ページへ分ける。
 */
export function planMixFetch(
  selection: MixSelection,
  pageLimit: number,
): MixFetchPlan {
  const hasUncapped = selection.uncappedSlugs.length > 0;
  const extras: MixExtraFetchPlan[] = [];
  let assignable = hasUncapped ? Math.max(0, pageLimit - 1) : pageLimit;
  const overflow = selection.extras.length > assignable;
  for (const [index, extra] of selection.extras.entries()) {
    const reserved = selection.extras.length - index - 1;
    const limit = overflow
      ? 1
      : Math.min(
          planMixExtraLimit(pageLimit, extra.pct),
          assignable - reserved,
        );
    extras.push(Object.freeze({ slug: extra.slug, limit }));
    assignable -= limit;
  }
  const extraTotal = extras.reduce((sum, plan) => sum + plan.limit, 0);
  return Object.freeze({
    uncappedLimit: hasUncapped ? Math.max(1, pageLimit - extraTotal) : null,
    extras: Object.freeze(extras),
  });
}

/** 全群を1件ずつ巡回するとき、同じ取得roundを表示する論理ページ数を返す。 */
export function getMixCatalogPagesPerRound(
  selection: MixSelection,
  pageLimit: number,
): number {
  const { uncappedLimit, extras } = planMixFetch(selection, pageLimit);
  const groupCount = extras.length + (uncappedLimit === null ? 0 : 1);
  return Math.max(1, Math.ceil(groupCount / pageLimit));
}

export type MixMergeInput = Readonly<{
  sort: SortType;
  pageLimit: number;
  page: number;
  /**
   * catalog-mixで取得した制御なし群。スレの`b`はサーバー付与を保持し、
   * 欠落分(アンカー板のshadowスレ合流)だけ先頭slugでタグ付けする。
   * 制御なしの板が1つも無い構成ではnull。
   */
  uncapped: Readonly<{ slugs: readonly string[]; catalog: Catalog }> | null;
  /** catalog=nullは取得失敗。その板抜きで縮退する。 */
  extras: readonly Readonly<{ slug: string; catalog: Catalog | null }>[];
}>;

/**
 * 制御なし群と%取り分板のカタログ応答を1ページへ混ぜる。
 *
 * - 制御なし群はサーバーが板をまたいだ並び順を保証済み。extrasには取得元板slug(`b`)を付与する
 * - new/old/repliesはThreadSummaryの客観値で全体を並べ直す
 * - bump/momentum/soudaneはサーバー順位を保ったまま比例間隔で interleave する
 *   (勢いの違う板が上部へ固まらないよう、各板をページ全体へ伸縮配置する)
 *
 * 各板の取得枠は`planMixFetch`で確定済みのため、ここでは板をまたいだ取捨選択を
 * しない(取り分板のスレ数が枠に満たないページは短くなるが、ページ間の連続性を優先する)。
 */
export function mergeMixCatalogs(input: MixMergeInput): Catalog {
  const merged = mergeMixCatalogRound(input);
  return { ...merged, threads: merged.threads.slice(0, input.pageLimit) };
}

/** 同じ取得roundの全群を既存の全体sort/interleaveで並べる。 */
export function mergeMixCatalogRound(input: MixMergeInput): Catalog {
  const anchorSlug = input.uncapped?.slugs[0] ?? "";
  const uncappedThreads =
    input.uncapped === null
      ? []
      : input.uncapped.catalog.threads.map((thread) => ({
          ...thread,
          b: thread.b ?? anchorSlug,
        }));
  const extraLists = input.extras.flatMap((extra) =>
    extra.catalog === null ? [] : [tagBoard(extra.catalog.threads, extra.slug)],
  );
  const lists =
    input.uncapped === null ? extraLists : [uncappedThreads, ...extraLists];

  const threads = isObjectiveSort(input.sort)
    ? sortObjectively(input.sort, lists.flat())
    : interleaveProportionally(lists);

  return {
    sort: input.uncapped?.catalog.sort ?? input.sort,
    threads,
    pagination: {
      page: input.uncapped?.catalog.pagination.page ?? input.page,
      limit: input.pageLimit,
      hasNextPage:
        (input.uncapped?.catalog.pagination.hasNextPage ?? false) ||
        input.extras.some((extra) => extra.catalog?.pagination.hasNextPage),
    },
  };
}

/** 取得済みroundの連続区間を、表示用の論理ページへ切り出す。 */
export function sliceMixCatalogRound(
  catalog: Catalog,
  start: number,
  pageLimit: number,
  page: number,
): Catalog {
  const end = start + pageLimit;
  return {
    ...catalog,
    threads: catalog.threads.slice(start, end),
    pagination: {
      page,
      limit: pageLimit,
      hasNextPage:
        end < catalog.threads.length || catalog.pagination.hasNextPage,
    },
  };
}

function tagBoard(
  threads: readonly ThreadSummary[],
  slug: string,
): ThreadSummary[] {
  return threads.map((thread) => ({ ...thread, b: slug }));
}

function isObjectiveSort(sort: SortType): boolean {
  return sort === "new" || sort === "old" || sort === "replies";
}

function sortObjectively(
  sort: SortType,
  threads: ThreadSummary[],
): ThreadSummary[] {
  const byCreatedAt = (thread: ThreadSummary): number =>
    instantFromIso(thread.createdAt)?.epochMilliseconds ?? 0;
  return threads.sort((a, b) => {
    if (sort === "old") return byCreatedAt(a) - byCreatedAt(b);
    if (sort === "replies") return b.replyCount - a.replyCount;
    return byCreatedAt(b) - byCreatedAt(a);
  });
}

/**
 * 各リストのサーバー順を保ったまま、リスト内順位の進み具合(0..1)で
 * 全体を並べ直す。同順位はリスト順(ホスト優先)で安定させる。
 */
function interleaveProportionally(
  lists: readonly (readonly ThreadSummary[])[],
): ThreadSummary[] {
  const entries = lists.flatMap((threads, listIndex) =>
    threads.map((thread, index) => ({
      thread,
      progress: (index + 0.5) / threads.length,
      listIndex,
    })),
  );
  return entries
    .sort((a, b) => a.progress - b.progress || a.listIndex - b.listIndex)
    .map((entry) => entry.thread);
}
